/* ============================================================
 *  Cart drawer coupons — discount code apply/remove
 *
 *  Logic is a 1:1 port of `setupDiscountForm()` in
 *  assets/mod-checkout.js so the drawer and the custom checkout
 *  share the exact same state machine (loading dots, error X, etc.).
 *
 *  Markup classes (cco-discount-*) match too.
 * ============================================================ */

(function () {
  if (window.__cartDrawerCouponsBooted) return;
  window.__cartDrawerCouponsBooted = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Single in-flight lock. Rapid clicks across different chips fire
     overlapping fetchCart()/postDiscount() requests whose responses race —
     the last write can land out of order, so a code silently fails to
     apply (and re-clicking "fixes" it). This flag serializes every
     coupon mutation (chip apply, remove, manual form, clear-all): while
     one is running, further clicks are ignored. */
  let couponBusy = false;

  /* Ask the theme which sections to bundle into the cart response so we get
     refreshed HTML alongside the cart data. The CartDrawer custom element listens
     to `cart:prepare-bundled-sections` and pushes its own section id. */
  function getBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      })
    );
    return sections;
  }

  async function postDiscount(code) {
    const res = await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code, sections: getBundledSections() }),
    });
    return res.ok ? await res.json() : null;
  }

  async function fetchCart() {
    const res = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin' });
    return res.ok ? await res.json() : null;
  }

  /* Like fetchCart() but includes bundled section HTML so the drawer can re-render. */
  async function fetchCartWithSections() {
    const sections = getBundledSections();
    const url = `${ROOT}cart.js?sections=${encodeURIComponent(sections.join(','))}`;
    const res = await fetch(url, { credentials: 'same-origin' });
    return res.ok ? await res.json() : null;
  }

  /* Multi-code: Shopify supports multiple discount codes if marked "combinable" in Admin.
     POST `discount: "CODE1,CODE2"` sets the new list. */
  function getCurrentCodes(cart) {
    return (cart?.discount_codes || [])
      .filter((d) => d.applicable)
      .map((d) => d.code);
  }

  /* Cart data from /cart/update.js includes a `sections` map (because we passed
     `sections: [...]` in the request body). Dispatch `cart:change` with the cart
     detail — the theme's CartDrawer._onCartChanged reads cart.sections[sectionId]
     and replaces its inner HTML automatically, preserving open state + listeners.

     Dispatch on document.documentElement (matches theme.js's convention at lines
     1973/2744): events fire there and bubble up through document → window.
     Dispatching on `document` directly only reaches listeners on document/window
     because documentElement (<html>) is a CHILD of document, not an ancestor,
     so it never sees the event. That mismatch was leaving mod-checkout's
     "reload on cart:change" listener (bound to documentElement) silent, which
     is why applying a chip code on the custom checkout page required a manual
     refresh before the discount line appeared. */
  function broadcastCartChange(cart) {
    if (!cart) return;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      detail: { cart, baseEvent: 'discount:applied' },
      bubbles: true,
    }));
  }

  /* Shopify keeps a `discount_code` cookie set whenever a code is applied (either via
     /discount/CODE URL or /cart/update.js). The cookie outlives cart-level removal —
     on the next full page load (e.g. navigating to /pages/checkout-custom), Shopify
     reads it and re-applies the discount to the freshly rendered cart.
     We aggressively clear all variations so the next page render sees no cookie. */
  function clearDiscountCookie() {
    const host = window.location.hostname;
    const rootDomain = host.replace(/^www\./, '');
    const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const paths = ['/', ''];
    const domains = ['', host, '.' + rootDomain, rootDomain];
    const names = ['discount_code', '_shopify_discount'];
    for (const name of names) {
      for (const path of paths) {
        for (const domain of domains) {
          let cookieStr = `${name}=; expires=${past}`;
          if (path) cookieStr += `; path=${path}`;
          if (domain) cookieStr += `; domain=${domain}`;
          try { document.cookie = cookieStr; } catch (_) {}
        }
      }
    }
  }

  /* ────── Click on a pre-defined code chip ────── */
  document.addEventListener('click', async (e) => {
    const chip = e.target.closest('.cart-drawer__coupon-chip[data-coupon-code]');
    if (!chip || chip.classList.contains('is-ineligible') || chip.classList.contains('is-applying')) return;
    e.preventDefault();
    if (couponBusy) return; // a coupon op is already running

    const code = chip.dataset.couponCode;
    if (!code) return;

    couponBusy = true;
    chip.classList.add('is-applying');
    try {
      const currentCart = await fetchCart();
      const existingCodes = getCurrentCodes(currentCart);
      if (existingCodes.length === 1 && existingCodes[0].toUpperCase() === code.toUpperCase()) {
        return; // already the only applied code — nothing to do
      }

      // REPLACE, not append. The store's tiered codes (VO50K/150K/200K)
      // aren't combinable, so appending a new code to the existing list
      // makes Shopify keep the FIRST applicable one and reject the rest —
      // which is why switching from a bigger code to a smaller one
      // silently failed. Posting only the clicked code swaps cleanly.
      const cart = await postDiscount(code);
      const matched = (cart?.discount_codes || []).find(
        (d) => d.code.toUpperCase() === code.toUpperCase()
      );
      if (!matched || !matched.applicable) {
        // New code didn't apply (e.g. min-subtotal not met) → restore previous.
        await postDiscount(existingCodes.join(','));
        return;
      }
      sessionStorage.removeItem('hpw_sf_cart_v1');
      broadcastCartChange(cart);
    } catch (err) {
      console.error('[cart-drawer-coupons] chip apply error:', err);
    } finally {
      chip.classList.remove('is-applying');
      couponBusy = false;
    }
  });

  /* ────── Manual input form (cloned from mod-checkout.js) ────── */
  function bindForm(form) {
    if (!form || form.dataset.couponsBound === '1') return;
    form.dataset.couponsBound = '1';

    const input = form.querySelector('[name="discountCode"]');
    const submitBtn = form.querySelector('[data-cco-discount-apply]');
    const inlineError = form.querySelector('[data-cco-discount-inline-error]');

    // Trạng thái nút+nhãn: 'applied' = XANH (thành công/đã áp), 'error' = ĐỎ, '' = reset.
    function setState(kind, msg) {
      submitBtn.classList.remove('is-loading', 'is-error', 'is-applied');
      submitBtn.disabled = false;
      if (kind === 'applied') submitBtn.classList.add('is-applied');
      else if (kind === 'error') submitBtn.classList.add('is-error');
      if (inlineError) inlineError.textContent = msg || '';
    }

    async function applyCode() {
      const code = (input.value || '').trim();
      if (!code) return;
      if (couponBusy) return;
      couponBusy = true;
      submitBtn.classList.remove('is-error', 'is-applied');
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      try {
        const currentCart = await fetchCart();
        const existingCodes = getCurrentCodes(currentCart);
        if (existingCodes.some((c) => c.toUpperCase() === code.toUpperCase())) {
          setState('applied', 'Mã đã áp dụng'); // XANH — đã áp rồi, KHÔNG phải lỗi
          return;
        }
        // REPLACE, not append — khớp với nút chip ở trên. Mã của store là
        // non-combinable (mã ship có shippingDiscounts:false; mã VO tiered
        // "Can't combine"). Nếu APPEND mã mới vào mã đang có, Shopify giữ mã cũ
        // và bỏ mã mới → applicable=false → hiện "Mã không hợp lệ" DÙ mã đúng.
        // Post riêng mã mới để thay thế sạch (fail thì khôi phục mã cũ bên dưới).
        const cart = await postDiscount(code);
        if (!cart) throw new Error('Lỗi mạng');
        const matched = (cart.discount_codes || []).find(
          (d) => d.code.toUpperCase() === code.toUpperCase()
        );
        if (!matched || !matched.applicable) {
          await postDiscount(existingCodes.join(','));
          throw new Error('invalid');
        }
        sessionStorage.removeItem('hpw_sf_cart_v1');
        broadcastCartChange(cart);
        setState('applied', 'Mã đã áp dụng'); // XANH — áp thành công
      } catch (err) {
        setState('error', 'Mã không hợp lệ'); // ĐỎ
      } finally {
        couponBusy = false;
      }
    }

    submitBtn?.addEventListener('click', () => {
      // In error state, the button acts as "clear" instead of re-applying
      if (submitBtn.classList.contains('is-error')) {
        input.value = '';
        submitBtn.classList.remove('is-error');
        input.focus();
        return;
      }
      applyCode();
    });

    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyCode();
      }
    });

    input?.addEventListener('input', () => {
      if (submitBtn.classList.contains('is-error') || submitBtn.classList.contains('is-applied')) {
        submitBtn.classList.remove('is-error', 'is-applied');
        if (inlineError) inlineError.textContent = '';
      }
    });
  }

  function bindAllForms() {
    document.querySelectorAll('[data-cco-discount-form]').forEach(bindForm);
  }

  /* Bind on initial load + every time the cart drawer re-renders */
  bindAllForms();
  document.addEventListener('DOMContentLoaded', bindAllForms);
  // Observer for dynamic drawer updates (re-render after apply/remove)
  const observer = new MutationObserver(() => bindAllForms());
  observer.observe(document.body, { childList: true, subtree: true });

  /* ────── Remove applied discount (X button next to badge) ────── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-remove-discount]');
    if (!btn) return;
    e.preventDefault();
    if (couponBusy) return;

    couponBusy = true;
    btn.disabled = true;
    btn.style.opacity = '0.4';
    try {
      // The X button removes ALL applied codes (simpler UX in the drawer).
      // For granular removal, switch to btn.dataset.code and filter that one out.
      const cart = await postDiscount(''); // clear discount via cart API (response includes sections)
      clearDiscountCookie(); // ALSO clear the Shopify discount cookie so the next
                             // full page load doesn't silently re-apply via cookie
      sessionStorage.removeItem('hpw_sf_cart_v1');
      broadcastCartChange(cart);
      // Backup: also fire cart:refresh which makes the drawer re-fetch its section
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      console.error('[cart-drawer-coupons] remove error:', err);
      btn.disabled = false;
      btn.style.opacity = '';
    } finally {
      couponBusy = false;
    }
  });

  /* ────── Clear all (header button in cart drawer) ──────
     Wipes every line item in one request. We use POST /cart/update.js with
     `updates[key]=0` for each item (rather than /cart/clear.js) because
     update.js accepts a `sections` parameter and returns rendered HTML,
     which lets the drawer + mod-checkout do their partial re-render via
     `cart:change` instead of forcing a page reload. */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-drawer-clear-all]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    if (couponBusy) return;

    if (!confirm('Xoá toàn bộ sản phẩm trong giỏ hàng?')) return;

    couponBusy = true;
    btn.disabled = true;
    btn.classList.add('is-clearing');
    try {
      const currentCart = await fetchCart();
      const items = currentCart?.items || [];
      if (items.length === 0) return; // nothing to clear

      const updates = {};
      items.forEach((item) => { updates[item.key] = 0; });

      const res = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates, sections: getBundledSections() }),
      });
      const cart = res.ok ? await res.json() : null;
      if (!cart) throw new Error('clear failed');

      clearDiscountCookie(); // discounts attached via cookie shouldn't survive a wipe
      sessionStorage.removeItem('hpw_sf_cart_v1');
      broadcastCartChange(cart);
      // Backup: same belt-and-suspenders refresh fire the remove handler uses
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (err) {
      console.error('[cart-drawer] clear all error:', err);
      btn.disabled = false;
      btn.classList.remove('is-clearing');
      alert('Không xoá được giỏ hàng. Vui lòng thử lại.');
    } finally {
      couponBusy = false;
    }
  });
})();
